import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useUser, UserRole } from '../contexts/UserContext';
import { apiService } from '../services/apiService';

interface KidDraft {
  firstName: string;
  grade: string;
  activity: string;
  teamNumber: string;
  schoolType: string;
}

const emptyKid = (): KidDraft => ({
  firstName: '',
  grade: '9th',
  activity: 'FRC',
  teamNumber: '',
  schoolType: 'Public',
});

const FamilySignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [step, setStep] = useState(1);
  const [kids, setKids] = useState<KidDraft[]>([emptyKid()]);
  const [region, setRegion] = useState('Any');
  const [targetTier, setTargetTier] = useState('Undecided');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/google`,
        { credential: credentialResponse.credential },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        const userData = response.data.user;
        const userRoles: UserRole[] =
          userData.roles && userData.roles.length > 0 ? userData.roles : ['parent'];
        login(response.data.token, {
          ...userData,
          userType: userData.userType || userRoles.join(','),
          roles: userRoles,
        });
        setStep(2);
      }
    } catch (error) {
      console.error('Google signup error:', error);
      alert('Failed to sign up with Google. Please try again.');
    }
  };

  const updateKid = (index: number, patch: Partial<KidDraft>) => {
    setKids((prev) => prev.map((k, i) => (i === index ? { ...k, ...patch } : k)));
  };

  const handleFinish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await apiService.updateFamilyProfile({
        children: kids
          .filter((k) => k.firstName.trim())
          .map((k) => ({
            firstName: k.firstName.trim(),
            grade: k.grade,
            activity: k.activity,
            teamNumber: k.teamNumber.trim() || undefined,
            schoolType: k.schoolType,
          })),
        preferences: { region, targetTier, note: note.trim() || undefined },
      });
    } catch (error) {
      console.error('Failed to save family profile:', error);
    }
    setSaving(false);
    navigate('/family-dashboard');
  };

  const wizClass = (n: number) => (n === step ? 'on' : n < step ? 'done' : '');

  return (
    <div className="section" style={{ paddingTop: 48 }}>
      <div className="wrap" style={{ maxWidth: 920 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
          Family account
        </span>
        <h1 style={{ fontSize: 30 }}>One family account. Every kid. One set of credits.</h1>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 10, maxWidth: '60ch' }}>
          A parent owns the account, billing, and permissions. Add each kid as a profile —
          sessions, matching, and pack credits are shared across the family.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '220px 1fr',
            gap: 40,
            alignItems: 'start',
            marginTop: 36,
          }}
        >
          <div className="wiz-nav" role="tablist" aria-label="Signup steps">
            <button className={wizClass(1)} onClick={() => setStep(1)}>
              1. Parent account{step > 1 ? ' ✓' : ''}
            </button>
            <button className={wizClass(2)} onClick={() => step > 1 && setStep(2)}>
              2. Add your kids{step > 2 ? ' ✓' : ''}
            </button>
            <button className={wizClass(3)} onClick={() => step > 2 && setStep(3)}>
              3. Matching preferences
            </button>
          </div>

          <div>
            {/* Step 1 */}
            {step === 1 && (
              <div className="card">
                <h3 style={{ marginBottom: 4 }}>Parent account</h3>
                <p className="meta" style={{ marginBottom: 20 }}>
                  Accounts are parent-owned by design — kids under 13 never hold an account.
                </p>
                {user ? (
                  <>
                    <p className="meta" style={{ marginBottom: 16 }}>
                      Signed in as <b style={{ fontWeight: 600 }}>{user.name}</b> ({user.email}).
                    </p>
                    <button className="btn btn-primary" onClick={() => setStep(2)}>
                      Continue
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => alert('Google sign-up failed. Please try again.')}
                      size="large"
                      theme="outline"
                      text="continue_with"
                      shape="rectangular"
                      width="320"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="card">
                <h3 style={{ marginBottom: 4 }}>Add your kids</h3>
                <p className="meta" style={{ marginBottom: 20 }}>
                  Each kid gets a profile under your account. You can add more later.
                </p>

                {kids.map((kid, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px solid var(--hairline)',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 12,
                    }}
                  >
                    <div className="row3">
                      <div className="field">
                        <label>First name</label>
                        <input
                          value={kid.firstName}
                          onChange={(e) => updateKid(i, { firstName: e.target.value })}
                        />
                      </div>
                      <div className="field">
                        <label>Grade</label>
                        <select
                          value={kid.grade}
                          onChange={(e) => updateKid(i, { grade: e.target.value })}
                        >
                          {['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((g) => (
                            <option key={g}>{g}</option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label>Activity</label>
                        <select
                          value={kid.activity}
                          onChange={(e) => updateKid(i, { activity: e.target.value })}
                        >
                          {['FRC', 'FTC', 'VEX', 'USACO', 'Deciding'].map((a) => (
                            <option key={a}>{a}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="row2">
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label>Team # (optional)</label>
                        <input
                          value={kid.teamNumber}
                          onChange={(e) => updateKid(i, { teamNumber: e.target.value })}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label>School type</label>
                        <select
                          value={kid.schoolType}
                          onChange={(e) => updateKid(i, { schoolType: e.target.value })}
                        >
                          <option>Public</option>
                          <option>Private</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="note" style={{ marginBottom: 12 }}>
                  Age 13–15 → supervised profile: you book and pay, and a parent joins every
                  session. Mentors see this rule and it's enforced in the room. Under 13 →
                  parent-only sessions (ask a veteran parent instead).
                </div>

                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginBottom: 20 }}
                  onClick={() => setKids((prev) => [...prev, emptyKid()])}
                >
                  + Add another kid
                </button>
                <div>
                  <button className="btn btn-primary" onClick={() => setStep(3)}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="card">
                <h3 style={{ marginBottom: 4 }}>Matching preferences</h3>
                <p className="meta" style={{ marginBottom: 20 }}>
                  We match on exact activity plus context. New verified mentors refresh your
                  matches automatically.
                </p>
                <div className="row2">
                  <div className="field">
                    <label>Region</label>
                    <select value={region} onChange={(e) => setRegion(e.target.value)}>
                      <option>Any</option>
                      <option>Pacific Northwest</option>
                      <option>California</option>
                      <option>Texas</option>
                      <option>Northeast</option>
                      <option>Midwest</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Target tier</label>
                    <select value={targetTier} onChange={(e) => setTargetTier(e.target.value)}>
                      <option>Undecided</option>
                      <option>Top-10 engineering</option>
                      <option>Top-25 national</option>
                      <option>Flagship state</option>
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label>What are you trying to figure out right now?</label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Whether Theo should commit to FTC next season, and how Marcus should frame team captaincy on his applications."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  ></textarea>
                </div>
                <button className="btn btn-primary" onClick={handleFinish} disabled={saving}>
                  {saving ? 'Creating…' : 'Create family account'}
                </button>
                <p className="meta" style={{ marginTop: 12 }}>
                  No subscription. You pay per session or buy a pack when you're ready.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilySignupPage;
